import type { ItemResponse } from '@palaccount/contracts';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Typeahead, type TypeaheadCreateNew } from '@/components/ui/typeahead';
import { QUICK_CREATE_ROSTER } from '@/features/quick-create/roster';
import { useCan } from '@/lib/permissions';
import type { PickerState } from '@/lib/typeahead';

import { ITEMS_KEY } from './api';

/**
 * Wave 4C — "+ New item" from inside a line's picker.
 *
 * The roster says an item is a JUMP entity (its form is too large for a
 * window), and its route and permission are read from there so this stays
 * one decision. What a grid adds is WHERE the jump lands: in a NEW TAB, so the
 * half-typed document is not navigated away from. When the clerk comes back
 * to this tab the items queries are refreshed once, and the new item is in
 * the picker to choose.
 */
let refreshArmed = false;
function refreshItemsOnReturn(queryClient: QueryClient) {
  if (refreshArmed || typeof window === 'undefined') return;
  refreshArmed = true;
  window.addEventListener(
    'focus',
    () => {
      refreshArmed = false;
      void queryClient.invalidateQueries({ queryKey: ITEMS_KEY });
    },
    { once: true },
  );
}

function useNewItemTab(disabled: boolean | undefined): TypeaheadCreateNew | undefined {
  const { t } = useTranslation();
  const entry = QUICK_CREATE_ROSTER.item;
  const allowed = useCan(entry.permission);
  const queryClient = useQueryClient();
  return useMemo(() => {
    if (!allowed || disabled === true || entry.mode !== 'jump') return undefined;
    return {
      label: t('picker.newItemTab'),
      onSelect: () => {
        window.open(entry.route, '_blank', 'noopener');
        refreshItemsOnReturn(queryClient);
      },
    };
  }, [allowed, disabled, entry, queryClient, t]);
}

/**
 * ONE item cell for every document grid (GEN-50).
 *
 * Ten line grids each drew their own `<select>` over the whole catalogue —
 * quotations, sales orders, invoices, delivery notes, purchase orders, bills,
 * goods receipts, both returns, stock adjustments and transfers. They are one
 * control now, because the parts that are easy to get wrong per grid are the
 * ones that matter: the ARIA label a screen reader and every test reads, the
 * free-text row that a document line is allowed to be, and the sentence an
 * empty result prints.
 *
 * The label stays the CALLER's, because it names the line ("Item, line 3") and
 * each screen numbers its own.
 */
export function ItemCell({
  label,
  clearLabel,
  picker,
  value,
  onPick,
  disabled,
}: {
  /** Already interpolated: "Item, line 3". */
  label: string;
  /** The free-text row, where a line without an item is legal. Omit where it is not. */
  clearLabel?: string | undefined;
  picker: PickerState<ItemResponse>;
  /** The line's item id — '' for a free-text line. */
  value: string;
  onPick: (itemId: string) => void;
  disabled?: boolean | undefined;
}) {
  const { t } = useTranslation();
  const createNew = useNewItemTab(disabled);
  return (
    <Typeahead
      size="cell"
      label={label}
      value={value || null}
      onChange={(id) => onPick(id ?? '')}
      options={picker.options}
      selected={picker.optionFor(value)}
      query={picker.query}
      onQueryChange={picker.setQuery}
      {...(clearLabel === undefined ? {} : { clearLabel })}
      emptyLabel={t('picker.noMatchingItem')}
      loading={picker.loading}
      // The options answer the typed text only once the debounce has caught
      // up AND the request is back — until then Enter waits (scanners).
      settled={picker.search === picker.query && !picker.loading}
      disabled={disabled}
      createNew={createNew}
    />
  );
}
